import { useTranslation } from 'next-i18next';
import { useContext } from 'react';
import { JoinModal } from '@dx-ui/osc-join-modal-button';

import {
  dispatchGlobalNavigationClick,
  sendInteractionReward,
  SpeechBubbleContext,
} from '../../../header.utilities';
import { HEADER_CLICK_IDS } from '../../../types';
import { sendReward } from '@dx-ui/framework-conductrics';
import { NavItemVisual, NavItem, NavItemWithLink } from '../../nav-items';
import { UserNavItemWithModalDefaultVariant } from './user-nav-item-with-modal';
import { useHeaderContext } from '../../../context/header-context';
import { useHeaderStateContext } from '../../../context/header-state-context';
import cx from 'classnames';

export const SignedOutAuthCluster = () => {
  const [t] = useTranslation(['osc-header', 'osc-login']);
  const {
    user: { loginOptions, onSignInAttempt, analytics },
    features: { shouldOpenJoinInPage, shouldOpenJoinInNewWindow },
    navigation: {
      languageSelectorOptions,
      onGlobalNavigationClick,
      isLegacyGlobalNavigationClickAnalyticsEnabled = true,
    },
  } = useHeaderContext();

  const {
    refs: { userButtonLinkRef },
    activeUserLinks: userLinks,
  } = useHeaderStateContext();

  const { isShowSpeechBubble, onDialogOpen } = useContext(SpeechBubbleContext);

  const defaultOptions = {
    title: t('osc-login:signIn'),
    onLoginAttempt: onSignInAttempt,
    sourceId: 'OHW' as const,
  };

  const hasBothNavLinks = !!userLinks?.signUpLink && !!userLinks?.signInLink;

  const renderSignUpButton = () => {
    if (!userLinks?.signUpLink) {
      return null;
    }

    if (shouldOpenJoinInPage) {
      return (
        <NavItemWithLink
          section="user"
          {...userLinks.signUpLink}
          underline={false}
          isNewWindow={shouldOpenJoinInNewWindow}
          showNewWindowIcon={false}
          label={t('signUp')}
          onClick={() => {
            sendInteractionReward();
            sendReward('nav-join-click');
            dispatchGlobalNavigationClick(
              HEADER_CLICK_IDS.Join,
              onGlobalNavigationClick,
              isLegacyGlobalNavigationClickAnalyticsEnabled
            );
          }}
          navItemClassName={cx({ 'osc-nav-list-divider relative': hasBothNavLinks })}
        />
      );
    }

    return (
      <NavItem
        data-testid="with-modal"
        className={cx('osc-nav-item-text', {
          'osc-nav-list-divider relative': hasBothNavLinks,
        })}
      >
        <JoinModal
          analytics={analytics}
          language={languageSelectorOptions?.locale || 'en'}
          onLoginAttempt={onSignInAttempt}
          dialogTrigger={
            <button
              data-testid="with-modal"
              type="button"
              className="text--sm group flex h-full items-center whitespace-nowrap px-1"
              onClick={() => {
                sendInteractionReward();
                sendReward('nav-join-click');
                dispatchGlobalNavigationClick(
                  HEADER_CLICK_IDS.Join,
                  onGlobalNavigationClick,
                  isLegacyGlobalNavigationClickAnalyticsEnabled
                );
              }}
            >
              <NavItemVisual label={t('signUp')} section="user" />
            </button>
          }
          onSuccessfulLogin={() => {
            setTimeout(() => userButtonLinkRef.current?.focus(), 1000);
          }}
        />
      </NavItem>
    );
  };

  return (
    <>
      {renderSignUpButton()}

      <UserNavItemWithModalDefaultVariant
        defaultOptions={defaultOptions}
        loginOptions={loginOptions}
        buttonLabel={t('osc-login:signIn')}
        showSpeechBubble={isShowSpeechBubble}
        onDialogOpen={onDialogOpen}
        onClick={() => {
          dispatchGlobalNavigationClick(
            HEADER_CLICK_IDS.SignIn,
            onGlobalNavigationClick,
            isLegacyGlobalNavigationClickAnalyticsEnabled
          );
          sendReward('nav-sign-in-click');
        }}
        navItemClassName={cx({ 'ms-[0.2ex]': hasBothNavLinks })}
        shouldOpenJoinInPage={shouldOpenJoinInPage}
        locale={languageSelectorOptions?.locale || 'en'}
      />
    </>
  );
};
